// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  TransferInstructionContextType,
  useTransferInstructionContext,
} from '@hooks/mutations/allocation/useTransferInstructionContext';
import { useExerciseSubmission } from '@hooks/mutations/ledger/useSubmission';
import { ChoiceContext } from '@daml.js/splice-api-token-metadata/lib/Splice/Api/Token/MetadataV1';
import {
  TransferInstruction,
  TransferInstruction_Withdraw,
} from '@daml.js/splice-api-token-transfer-instruction/lib/Splice/Api/Token/TransferInstructionV1';
import { ContractId } from '@daml/types';
import { toTokenStandardDisclosedContract } from '../../../../utils/common';
import { ERR_WITHDRRAW_INSTRUCT_XFER, SUC_WITHDRAW_INSTRUCT_XFER } from '../../../../utils/strings';

interface Args {
  registrar: string;
  transferCid: ContractId<TransferInstruction>;
}
export const useWithdrawTransferInstruction = () => {
  const { submit: withdrawTransferInstruction } = useExerciseSubmission(
    TransferInstruction.TransferInstruction_Withdraw,
    SUC_WITHDRAW_INSTRUCT_XFER,
    ERR_WITHDRRAW_INSTRUCT_XFER,
  );
  const getWithdrawTransferInstructionContext = useTransferInstructionContext(
    TransferInstructionContextType.Withdraw,
  );
  const accept = async (args: Args) => {
    const { registrar, transferCid } = args;
    const withdrawTransferInstructionContext =
      await getWithdrawTransferInstructionContext.mutateAsync({
        registrar,
        transferInstructionCid: transferCid,
      });

    const payload: TransferInstruction_Withdraw = {
      extraArgs: {
        context: withdrawTransferInstructionContext.choiceContextData as ChoiceContext,
        meta: { values: {} },
      },
    };
    const disclosedContracts =
      withdrawTransferInstructionContext.disclosedContracts.map(toTokenStandardDisclosedContract) ||
      [];
    await withdrawTransferInstruction({
      cid: transferCid,
      argument: payload,
      meta: {
        disclosedContracts,
      },
    });
  };
  return accept;
};
